/* globals grecaptcha App */

import axios from 'axios';
import serialize from 'form-serialize';
import { clonedNodeToElement, getOptionsFromElement, getPosition, htmlToElement, isOutOfViewport, renderAsElement } from './utils';
import { defaults, isObject, isString } from 'lodash';

export class Form {
  ajax = false;
  delay = null;

  constructor(element, settings) {
    this.settings = settings;
    this.element = element;
    this.init();
    console.log(`Form ${element.getAttribute('id')} initialised`);
  }

  init() {
    this.formErrors = this.element.querySelector(this.settings.selectors.formErrors);
    this.successBlock = this.element.querySelector(this.settings.selectors.successBlock);
    this.formWrapper = this.element.querySelector(this.settings.selectors.formWrapper);
    this.submitWrapper = this.element.querySelector(this.settings.selectors.submitWrapper);
    this.submitElement = this.element.querySelector(this.settings.selectors.submit);
    this.confirmationPopup = document.querySelector(this.settings.selectors.confirmationPopup);
    this.terms = false;
    if (this.settings.agreeTerms) {
      this.terms = this.element.querySelector(this.settings.agreeTerms);
    }
    this.originalForm = this.element.cloneNode(true);
    this.fields = this.getFields();
    if (this.settings.noValidate) {
      this.element.setAttribute('novalidate', true);
    }

    if (this.element.hasAttribute('data-form-ajax')) {
      this.ajax = true;
    }

    if (this.settings.confirmationPopup && this.confirmationPopup) {
      this.confirmationPopup.querySelector('.js-confirm').addEventListener('click', () => {
        this.submit();
        this.confirmationPopup.classList.add('hidden');
      });
      this.confirmationPopup.querySelector('.js-cancel').addEventListener('click', () => {
        this.confirmationPopup.classList.add('hidden');
      });
    }

    this.initSubmit();

    this.element.addEventListener(
      'submit',
      (event) => {
        this.submitHandler(event);
      },
      false,
    );
    this.element.addEventListener('click', this.resetHandler.bind(this), false);
    this.element.addEventListener('change', this.valueChangeHandler.bind(this), true);
    this.element.addEventListener('input', this.valueChangeHandler.bind(this), true);
  }

  submitHandler(event) {
    if (this.settings.confirmationPopup) {
      event.preventDefault();
      this.confirmationPopup.classList.remove('hidden');
      return;
    }

    if (this.terms && !this.terms.checked) {
      event.preventDefault();
      alert(this.settings.termsMessage);
      return;
    }

    if (this.settings.confirmMessage) {
      let confirmation = confirm(this.settings.confirmMessage);
      if (!confirmation) {
        event.preventDefault();
        return;
      }
    }

    if (this.settings.ajax) {
      event.preventDefault();
    }

    this.submit();
  }

  valueChangeHandler(event) {
    clearTimeout(this.delay);
    if (event.type === 'change') {
      this.initSubmit();
    } else {
      this.delay = setTimeout(() => {
        this.initSubmit();
      }, 250);
    }
  }

  resetHandler(event) {
    let reset = event.target.closest('[data-form-reset]');
    if (!reset) return;
    event.preventDefault();
    this.reset();
  }

  reset() {
    let newFormElement = clonedNodeToElement(this.originalForm);
    this.element.parentNode.replaceChild(newFormElement, this.element);
    this.element = newFormElement;
    this.settings = defaults(getOptionsFromElement(newFormElement), App.forms.defaults.form);
    this.init();
  }

  beforeSubmit() {
    return new Promise((resolve, reject) => {
      let recaptchaField = this.getField('g-recaptcha-3-response');
      if (recaptchaField) {
        grecaptcha.ready(() => {
          grecaptcha.execute(App.settings.recaptcha, { action: this.settings.recaptchaAction || 'login' }).then((token) => {
            recaptchaField.value = token;
            resolve();
          });
        });
      } else {
        resolve();
      }
    });
  }

  submit() {
    this.submitWrapper.classList.add(this.settings.submittingClass);
    this.disableSubmit();
    this.settings.onSubmit();
    this.clearErrors();
    this.beforeSubmit().then(
      () => {
        let data = this.getSubmitUrlAndData();
        axios
          .post(data.url, data.data, {
            headers: {
              Accept: 'application/json',
            },
          })
          .then((response) => {
            let data = response.data;
            this.settings.onSuccess(data);
            let redirect = false;
            let store = false;
            if (this.settings.redirect) {
              if (data.redirect) {
                redirect = data.redirect;
                store = true;
              } else {
                throw new Error('Ajax call did not provide a redirect url');
              }
            }
            this.showToast(this.settings.successMessage ? this.settings.successMessage : data.message || 'Success', 'success', store);
            if (redirect) {
              window.location.href = redirect;
              return;
            }
            this.showSuccessBlock();
            this.afterSubmit();
          })
          .catch((error) => {
            let data = error.response ? error.response.data : {};
            this.showToast(this.settings.errorMessage || data.message, 'error', false);
            this.addFormError(this.settings.errorMessage || data.message);
            this.addFormErrors(data.errors);
            this.resetRecaptcha();
            this.focusOnFirstError(data);
            this.settings.onFail(data);
            this.afterSubmit();
          });
      },
      () => {
        this.afterSubmit();
      },
    );
  }

  getSubmitUrlAndData() {
    let data;
    if (this.settings.serialize) {
      data = serialize(this.element, {
        hash: true,
        empty: true,
      });
    } else {
      data = new FormData(this.element);
    }
    let url = this.element.getAttribute('action') || '/';
    if ((this.settings.serialize || true) && data.action) {
      url = '/?action=' + data.action;
      delete data.action;
    }
    return { url: url, data: data };
  }

  showSuccessBlock() {
    if (this.settings.showSuccessBlock && this.successBlock) {
      this.successBlock.classList.remove('hidden');
      this.formWrapper.classList.add('hidden');
    }
  }

  showToast(message, type = 'success', store = true) {
    if (type == 'success' && !this.settings.toastOnSuccess) {
      return;
    }
    if (type == 'error' && !this.settings.toastOnError) {
      return;
    }
    if (!message) {
      return;
    }
    App.toasts.create({
      type: type,
      dismissable: true,
      message: message,
      store: store,
    });
  }

  afterSubmit() {
    this.submitWrapper.classList.remove(this.settings.submittingClass);
    this.initSubmit();
  }

  addFormError(error) {
    if (!this.settings.formErrors || !error) return;
    let html = htmlToElement('<ul><li>' + error + '</li></ul>');
    this.formErrors.append(html);
    this.element.classList.add(this.settings.hasErrorsClass);
  }

  addFormErrors(errors) {
    if (!errors) {
      return;
    }
    // Collate form errors (could be string or array)
    let errorArray = [];
    Object.keys(errors).forEach((handle) => {
      let regex = /(.+)\[([0-9]+)\]\[(.+)\]/;
      let matches = handle.match(regex);
      let errorObject = {};
      if (matches != null) {
        errorObject['parent'] = matches[1];
        errorObject['row'] = parseInt(matches[2]);
        errorObject['handle'] = matches[3];
      } else {
        errorObject['handle'] = handle;
      }
      if (typeof errors[handle] !== 'array') {
        errorObject['message'] = errors[handle];
        errorArray.push(errorObject);
      } else {
        errors[handle].forEach((error) => {
          errorObject['message'] = error;
          errorArray.push(errorObject);
        });
      }
    });

    errorArray.forEach((error) => {
      this.addFieldError(error);
      this.highlightFieldError(error);
    });
  }

  highlightFieldError(error) {
    let field = this.getFieldHolder(error);
    if (!field) return;
    if (this.settings.highlightFieldErrors && field) {
      field.classList.add(this.settings.hasErrorClass);
    }
  }

  addFieldError(error, field) {
    if (!this.settings.fieldErrors) {
      return;
    }
    let message = '';
    let elementForErrors;
    if (isObject(error)) {
      let field = this.getFieldHolder(error);
      if (!field) return;
      message = error.message;
      if (error.hasOwnProperty('parent')) {
        elementForErrors = field.closest(this.settings.selectors.fieldRow);
      } else {
        elementForErrors = field;
      }
    } else if (isString(error) && field) {
      elementForErrors = field;
      message = error;
    }
    if (!elementForErrors || !message) {
      return;
    }
    if (typeof message == 'array') {
      message.forEach((mess) => {
        this.addFieldError(mess, elementForErrors);
      });
      return;
    }
    let html = '<p class="' + this.settings.fieldErrorClass + '" data-form-field-error>' + message + '</p>';
    elementForErrors.append(renderAsElement(html));
  }

  resetRecaptcha() {
    let recaptchaField = this.getField('g-recaptcha-response');
    if (recaptchaField && typeof grecaptcha !== 'undefined' && grecaptcha !== null) {
      grecaptcha.reset();
    }
  }

  focusOnFirstError(data) {
    let scrollTo = false;
    if (this.formErrors && this.formErrors.childElementCount > 0) {
      if (isOutOfViewport(this.formErrors).any) {
        let position = getPosition(this.formErrors);
        scrollTo = position.top;
      }
    } else if (data.errors && (this.settings.fieldErrors || this.settings.highlightFieldErrors)) {
      let firstError = this.element.querySelector('.' + this.settings.hasErrorClass);
      if (firstError) {
        if (isOutOfViewport(firstError).any) {
          let position = getPosition(firstError);
          scrollTo = position.top;
        }
      }
    }
    if (scrollTo) {
      window.scrollTo({
        top: scrollTo - this.settings.offset,
        behavior: 'smooth',
      });
    }
  }

  getFieldHolder(params) {
    let isChildFieldError = params.hasOwnProperty('parent');
    let name = isChildFieldError ? params.parent : params.handle;
    let field = this.getField(name);
    if (field && isChildFieldError) {
      let fieldRows = field.querySelectorAll(this.settings.selectors.fieldRow);
      let fieldRow = fieldRows.length > 0 ? fieldRows[params.row] : false;
      field = fieldRow ? this.getField(params.handle, fieldRow) : false;
    }
    if (!field) return false;
    let holder = field.closest(this.settings.selectors.field);
    if (!holder) return false;
    return holder;
  }

  getField(name, context) {
    if (!context) {
      context = this.element;
    }
    if (name.indexOf('.') > -1) {
      let parts = name.split('.');
      let firstPart = parts.shift();
      name = firstPart + '[' + parts.join('][') + ']';
    }
    let query = [
      '[name="' + CSS.escape(name) + '"]',
      '[name*=' + CSS.escape('[' + name + ']') + ']',
      '[name="' + this.formatFieldName(name, '', '[]') + '"]',
      '[name="' + this.formatFieldName(name, 'address') + '"]',
      '[name="' + this.formatFieldName(name, 'address', '[]') + '"]',
      '[name="' + this.formatFieldName(name, 'fields') + '"]',
      '[name="' + this.formatFieldName(name, 'fields', '[]') + '"]',
      '[name*="' + this.formatFieldName(name, '[fields]') + '"]',
      '[name*="' + this.formatFieldName(name, '[fields]', '[]') + '"]',
      '[data-form-field="' + CSS.escape(name) + '"]',
      '[data-form-field="' + this.formatFieldName(name, '', '[]') + '"]',
      '[data-form-field="' + this.formatFieldName(name, 'fields') + '"]',
      '[data-form-field="' + this.formatFieldName(name, 'fields', '[]') + '"]',
      '[data-form-field*="' + this.formatFieldName(name, '[fields]') + '"]',
      '[data-form-field*="' + this.formatFieldName(name, '[fields]', '[]') + '"]',
    ];
    return context.querySelector(query.join(', '));
  }

  formatFieldName(name, prefix, postfix) {
    if (!name || typeof name != 'string') return '';
    prefix = typeof prefix === 'string' ? prefix : '';
    postfix = typeof postfix === 'string' ? postfix : '';
    let parts = name.match(/\w+/g);
    return CSS.escape(prefix + '[' + parts.join('][') + ']' + postfix);
  }

  clearErrors() {
    if (this.formErrors) {
      this.formErrors.innerHTML = '';
    }
    this.element.classList.remove(this.settings.hasErrorsClass);
    let fieldErrors = this.element.querySelectorAll(this.settings.selectors.fieldError);
    if (fieldErrors) {
      fieldErrors.forEach((message) => {
        message.remove();
      });
    }
    let fieldsWithErrorClass = this.element.querySelectorAll('.' + this.settings.hasErrorClass);
    if (fieldsWithErrorClass) {
      fieldsWithErrorClass.forEach((field) => {
        field.classList.remove(this.settings.hasErrorClass);
      });
    }
  }

  getFields() {
    let elements = this.element.elements;
    let fields = [];
    if (elements.length) {
      for (let i = 0; i < elements.length; i++) {
        if (elements[i].type !== 'reset' && elements[i].type !== 'submit' && elements[i].type !== 'button' && elements[i].type !== 'hidden') {
          fields.push(elements[i]);
        }
      }
    }
    return fields;
  }

  initSubmit() {
    if (this.terms) {
      if (!this.terms.checked) {
        this.submitElement.classList.add('opacity-30');
      } else {
        this.submitElement.classList.remove('opacity-30');
      }
    }
    if (this.settings.disableSubmit) {
      // Any required fields without a value
      let emptyRequiredFields = false;
      if (this.fields.length) {
        for (let i = 0; i < this.fields.length; i++) {
          let field = this.fields[i];
          if (field.validity.valueMissing) {
            emptyRequiredFields = true;
            break;
          }
        }
        if (emptyRequiredFields) {
          this.disableSubmit();
          return;
        }
      }
    }
    this.enableSubmit();
  }

  enableSubmit() {
    if (this.submitElement) {
      this.submitElement.removeAttribute('disabled');
    }
  }

  disableSubmit(form) {
    if (this.submitElement) {
      this.submitElement.setAttribute('disabled', true);
    }
  }

  fieldsHaveChanges(inputs) {
    if (!inputs) {
      return false;
    }
    var input,
      i,
      l = [];
    for (i = 0, l = inputs.length; i < l; i++) {
      input = inputs[i];
      if (input.nodeName.toLowerCase() == 'input') {
        switch (input.type) {
          case 'text':
          case 'textarea':
          case 'password':
          case 'search':
          case 'email':
          case 'url':
          case 'tel':
          case 'number':
          case 'range':
          case 'date':
          case 'month':
          case 'week':
          case 'time':
          case 'datetime':
          case 'datetime-local':
          case 'color':
            if (input.value != input.defaultValue) {
              return true;
            }
            break;
          case 'checkbox':
          case 'radio':
            if (input.defaultChecked != input.checked) {
              return true;
            }
            break;
        }
      } else {
        for (var x = 0; x < this.length; x++) {
          if (this.options[x].selected != this.options[x].defaultSelected) {
            return true;
          }
        }
      }
    }
    return false;
  }
}
